<template>
  <div class="mt-5">
    <validation-observer ref="form" @change="onFormChange">
      <div v-for="question in questionAnswers" :key="question.id" class="mb-5">
        <p class="font-14 font-weight-bold">{{ question.name }}</p>
        <validation-provider
          v-slot="{ errors }"
          :name="'question-' + question.id"
          rules="required"
        >
          <CustomRadioQuestion
            :options="question.answers"
            v-model="question.model"
            :error="errors[0]"
            custom-error-msg="Por favor seleciona una respuesta."
            :disabled="readOnly"
            @change="onResponse(question, $event)"
          >
            <template v-slot:slot1>
              <div class="mt-3">
                <div class="row">
                  <div class="col-md-3 col-12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      mode="lazy"
                      rules="required"
                      :custom-messages="{
                        required: 'Favor seleccione el Grado.'
                      }"
                      @change="onFormChange"
                    >
                      <CustomDropDown
                        v-model="selectedGrade"
                        :options="grades"
                        fieldkey="id"
                        fieldname="name"
                        placeholder="Seleccione el Grado"
                        class="bold-label"
                        :error="errors[0]"
                        :custom-error-msg.sync="errors[0]"
                        :disabled="readOnly"
                        @input="onFormChange"
                      />
                    </ValidationProvider>
                  </div>
                  <div v-if="hasDifferentGrade" class="col">
                    <div class="panel-yellow d-flex align-items-center p-2">
                      <i class="fas fa-bell-exclamation icon" />
                      <span class="content">
                        El grado seleccionado no es el mismo o el próximo según
                        los datos en el sistema.
                      </span>
                    </div>
                  </div>
                </div>
                <div class="my-2" v-if="hasDifferentGrade">
                  <ValidationProvider
                    v-slot="{ errors }"
                    slim
                    rules="required"
                    name="differentGradeReason"
                  >
                    <label class="data-label">
                      Razón diferencia en grado:
                    </label>
                    <custom-text-area
                      v-model="differentGradeReason"
                      :placeholder="''"
                      :maxlength="300"
                      class="text-right"
                      :error="errors[0]"
                      custom-error-msg="La Razón diferencia en grado es requerida."
                      show-char-count
                    />
                  </ValidationProvider>
                </div>
              </div>
            </template>
          </CustomRadioQuestion>
        </validation-provider>
      </div>
      <div
        v-for="question in otherQuestionAnswers"
        :key="question.id"
        class="mb-5"
      >
        <p class="font-14 font-weight-bold">{{ question.name }}</p>
        <ValidationProvider
          v-slot="{ errors }"
          mode="lazy"
          rules="required"
          :custom-messages="{
            required: 'Favor escriba otros medios de lectura.'
          }"
        >
          <CustomInput
            v-model="question.model"
            regular-label
            :error="errors[0]"
            :custom-error-msg="errors[0]"
            class="w-25"
            :disabled="readOnly"
          />
        </ValidationProvider>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import {
  getQuestionAnswers,
  getSchoolgrade
} from "@/services/studentVisualImpairments.js";
import CustomRadioQuestion from "@/components/common/form/CustomRadioQuestion.vue";
import studentVisualImpeirmentsQuestionAnswer from "@/utils/constants/student/studentVisualImpeirmentsQuestionAnswer";
import studentVisualImpeirmentsQuestionType from "@/utils/constants/student/studentVisualImpeirmentsQuestionType";
import CustomDropDown from "@/components/common/CustomDropDown";
import CustomInput from "CustomInput";
import CustomTextArea from "CustomTextArea";
import Error from "custom-error";
import alert from "@/mixin/alert.mixin";

export default {
  name: "QuestionAnswer",
  mixins: [alert],
  components: {
    CustomRadioQuestion,
    CustomDropDown,
    CustomInput,
    CustomTextArea,
    Error
  },
  props: {
    studentGrade: {
      type: Number,
      default: () => 0
    },
    value: {
      type: Object,
      required: true,
      default: () => ({
        questionAnswers: [],
        otherQuestionAnswers: [],
        gradeId: null
      })
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      options: [],
      questionAnswers: [],
      otherQuestionAnswers: [],
      grades: [],
      selectedGrade: null,
      differentGradeReason: null
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    hasDifferentGrade() {
      return (
        Boolean(this.selectedGrade) &&
        this.studentGrade !== this.selectedGrade.id
      );
    }
  },
  async mounted() {
    await this.getSchoolgrade();
    await this.getQuestionAnswers();
  },
  methods: {
    async getQuestionAnswers() {
      const { data } = await getQuestionAnswers();
      this.options = data;
      this.setOptions();
    },
    async getSchoolgrade() {
      const { data } = await getSchoolgrade();
      this.grades = data.map(item => {
        return {
          id: item.id,
          name: item.edenGradeDescSpanish
        };
      });
      this.selectedGrade = this.grades.find(x => x.id === this.value.gradeId);
    },
    onFormChange() {
      this.setModel();
    },
    onResponse(question, current) {
      const answers = [
        [8, 13, 20],
        [9, 14, 22],
        [10, 15, 23],
        [11, 16, 24],
        [12, 17, 25]
      ];

      const identical = answers.find(x => x.includes(current));
      let existing = false;

      if (identical) {
        identical.every(element => {
          existing = this.model.questionAnswers.some(
            x => x.answerId == element && x.questionId != question.id
          );
          return !existing;
        });
      }

      if (existing) {
        this.ShowToast(
          "Alerta",
          "No es posible seleccionar ya que se encuentra como otro medio de lectura",
          "error"
        );
        question.model = null;
        return;
      }

      this.setModel();
    },
    setOptions() {
      this.questionAnswers = this.options
        .filter(x => x.type == studentVisualImpeirmentsQuestionType.Radio)
        .map(item => {
          return {
            ...item,
            model: this.value.questionAnswers.find(
              x => x.questionId === item.id
            )?.answerId,
            answers: item.answers.map(answer => {
              return {
                value: answer.id,
                title: answer.name,
                label: answer.description,
                slot:
                  answer.id ===
                  studentVisualImpeirmentsQuestionAnswer.PrimerGrado11moGrado
                    ? "slot1"
                    : null
              };
            })
          };
        });
      this.otherQuestionAnswers = this.options
        .filter(x => x.type == studentVisualImpeirmentsQuestionType.Text)
        .map(x => ({
          ...x,
          model: this.value.otherQuestionAnswers.find(
            y => y.questionId === x.id
          )?.answer
        }));
      this.selectedGrade = this.grades.find(x => x.id === this.value.gradeId);
      this.differentGradeReason = this.value.differentGradeReason;
    },
    setModel() {
      const value = {
        questionAnswers: this.questionAnswers.map(x => ({
          questionId: x.id,
          answerId: x.model
        })),
        otherQuestionAnswers: this.otherQuestionAnswers.map(x => ({
          questionId: x.id,
          answerId: x.answers[0]?.id,
          answer: x.model
        })),
        gradeId: this.selectedGrade?.id,
        differentGradeReason: this.differentGradeReason
      };
      this.model = value;
    }
  },
  watch: {
    value: {
      handler() {
        this.setOptions();
      },
      deep: true
    },
    options: {
      handler() {
        this.setOptions();
        this.setModel();
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
.panel-yellow .content {
  color: #af6708;
  margin-left: 12px;
  text-align: left;
}
.panel-yellow .icon {
  font-size: 18px;
  color: #af6708;
  margin-top: 3px;
}
.panel-yellow {
  display: flex;
  flex-direction: row;
  background: #fff5e8;
  border: 1px solid #fdac41;
  border-radius: 5px;
}
</style>
